/**
 * AI admin API helpers — PMA-AI-008/PMA-AI-009.
 *
 * All calls go through the shared apiClient (axios + Firebase ID-token
 * interceptor). Never import this from a non-admin page — the routes are
 * guarded by ProtectedRoute allowedRoles=["admin"] in App.tsx.
 */

import { apiClient } from "./api";
import { LONG_TIMEOUT_MS } from "./httpTimeouts";

// ── Types ────────────────────────────────────────────────────────────────────

export interface ProviderConfig {
  /** Masked form only — e.g. "****abcd". Never a full plaintext key. */
  api_key_masked?: string;
  default_model?: string;
}

export interface FeatureToggles {
  narrative: boolean;
  anomaly_sweep: boolean;
  nl_query: boolean;
  /** EPIC-112: AI match-sheet import (opt-in, default off). */
  match_sheet_import?: boolean;
}

/** Returned by GET /admin/ai/settings when encryption key IS configured. */
export interface AiSettings {
  providers: {
    gemini?: ProviderConfig;
    openrouter?: ProviderConfig;
    anthropic?: ProviderConfig;
  };
  active_provider: string;
  feature_toggles: FeatureToggles;
  nl_query_tool_allowlist: string[];
  /** EPIC-112: read each sheet twice and flag disagreements (default true when absent). */
  match_sheet_import_double_read?: boolean;
  updated_by?: string;
  updated_at?: string;
}

/** Returned by GET /admin/ai/settings when the server encryption key is unset. */
export interface AiSettingsNotConfigured {
  configured: false;
  warning: string;
}

export type AiSettingsResponse = AiSettings | AiSettingsNotConfigured;

export interface ProviderUpdatePayload {
  /** Include ONLY when the admin typed a new plaintext key. */
  api_key?: string;
  default_model?: string;
  /** EPIC-115: delete the stored key (default_model is kept). Never combine with api_key. */
  remove_api_key?: boolean;
}

export interface AiSettingsPutBody {
  active_provider?: string;
  providers?: {
    gemini?: ProviderUpdatePayload;
    openrouter?: ProviderUpdatePayload;
    anthropic?: ProviderUpdatePayload;
  };
  feature_toggles?: Partial<FeatureToggles>;
  nl_query_tool_allowlist?: string[];
  match_sheet_import_double_read?: boolean;
}

/**
 * EPIC-95 amendment ("Tool-capable model selection", 2026-09-23): the
 * backend now lists only tool-capable models per provider (OpenRouter:
 * `supported_parameters` includes "tools"; Gemini: native `generateContent`
 * minus non-chat families), each carrying whether it accepts image input.
 */
export interface AiModelInfo {
  id: string;
  supports_images: boolean;
}

export interface ModelsResponse {
  provider: string;
  models: AiModelInfo[];
  warning?: string;
}

/**
 * POST /admin/ai/models/verify body — one minimal forced tool call against
 * the live provider (team_plan.md "Tool-capable model selection"). Never
 * assume a listed or previously-saved model still works without this: the
 * list can go stale the moment a provider retires a model (EPIC-95
 * criterion-10 live check: Google retired gemini-2.0-flash/2.5-flash/
 * 2.5-flash-lite out from under a dev deployment that had one saved).
 */
export interface VerifyModelSuccess {
  ok: true;
  supports_images: boolean;
}

export type VerifyModelFailureReason =
  | "retired"
  | "no_tool_call"
  | "provider_error";

export interface VerifyModelFailure {
  ok: false;
  reason: VerifyModelFailureReason;
  message: string;
}

export type VerifyModelResult = VerifyModelSuccess | VerifyModelFailure;

/**
 * The structured 422 body PUT /admin/ai/settings raises when the backend's
 * forced-tool-call probe rejects the requested `default_model` (retired,
 * refuses tools, or returns no tool call). Nothing is saved when this fires.
 */
export interface ModelNotToolCapableDetail {
  code: "model_not_tool_capable";
  model: string;
  message: string;
}

export interface TokenUsage {
  prompt_tokens?: number;
  completion_tokens?: number;
  total_tokens: number;
}

export interface PlaygroundResponse {
  text: string;
  token_usage: TokenUsage;
  latency_ms: number;
  tool_calls?: unknown[];
}

export interface PlaygroundPayload {
  prompt: string;
  provider?: string;
  model?: string;
  tools?: string[];
}

// ── Default values ────────────────────────────────────────────────────────────

export const DEFAULT_TOOL_ALLOWLIST = [
  "get_advanced_metrics",
  "get_events_filtered",
  "get_player_stats",
];

export const DEFAULT_SETTINGS: AiSettings = {
  providers: { gemini: {}, openrouter: {}, anthropic: {} },
  active_provider: "gemini",
  feature_toggles: {
    narrative: false,
    anomaly_sweep: false,
    nl_query: false,
    match_sheet_import: false,
  },
  match_sheet_import_double_read: true,
  nl_query_tool_allowlist: [...DEFAULT_TOOL_ALLOWLIST],
};

// ── API functions ─────────────────────────────────────────────────────────────

/** One feature's gate result inside {@link AiStatus} (contract v2). */
export interface AiFeatureStatus {
  feature: string;
  active: boolean;
  /** Server's English reason; null when active. */
  reason: string | null;
}

/**
 * Returned by GET /admin/ai/status (EPIC-91, contract v2). Never carries key
 * material, only whether the active provider has a stored key.
 */
export interface AiStatus {
  /** At least one feature is active. */
  active: boolean;
  /** Shared English gate reason when every feature is blocked for the same one, else null. */
  reason: string | null;
  checked_feature: string;
  /** Active provider, null when no settings document exists. */
  provider: string | null;
  provider_key_present: boolean;
  features: AiFeatureStatus[];
}

/**
 * GET /admin/ai/status
 * Whether the AI gate would let a feature run, and why not when it would not.
 */
export async function getAiStatus(): Promise<AiStatus> {
  return apiClient.get<AiStatus>("/admin/ai/status");
}

/**
 * GET /admin/ai/settings
 * Returns AiSettings on 200, AiSettingsNotConfigured if encryption key unset,
 * or DEFAULT_SETTINGS when the backend returns 404 (no doc yet).
 */
export async function getAiSettings(): Promise<AiSettingsResponse> {
  try {
    return await apiClient.get<AiSettingsResponse>("/admin/ai/settings");
  } catch (err: unknown) {
    const status = (err as { response?: { status?: number } })?.response
      ?.status;
    if (status === 404) {
      return { ...DEFAULT_SETTINGS };
    }
    throw err;
  }
}

/**
 * PUT /admin/ai/settings
 * Returns the updated masked AiSettings doc.
 * IMPORTANT: only include api_key in providers when the admin typed a new key.
 * LONG_TIMEOUT_MS: a changed `default_model` now runs a live forced
 * tool-call probe against the provider before saving (EPIC-95 amendment) —
 * same upstream-LLM-round-trip justification as `verifyModel` below.
 * Throws the raw axios error on any non-2xx response; a 422 carries
 * `ModelNotToolCapableDetail` as `response.data.detail` — read it with
 * `getModelNotToolCapableDetail` rather than assuming `.message`.
 */
export async function putAiSettings(
  body: AiSettingsPutBody,
): Promise<AiSettings> {
  return apiClient.put<AiSettings>("/admin/ai/settings", body, {
    timeout: LONG_TIMEOUT_MS,
  });
}

/**
 * Narrows an error thrown by `putAiSettings` to the structured 422 raised
 * when the save-time tool-call probe rejects the requested model. Returns
 * null for every other error shape (plain-string detail, network error,
 * a 4xx/5xx unrelated to this check) — callers fall back to a generic
 * error message in that case.
 */
export function getModelNotToolCapableDetail(
  err: unknown,
): ModelNotToolCapableDetail | null {
  const response = (err as { response?: { status?: number; data?: unknown } })
    ?.response;
  if (response?.status !== 422) return null;
  const detail = (response.data as { detail?: unknown } | undefined)?.detail;
  if (
    detail &&
    typeof detail === "object" &&
    (detail as { code?: unknown }).code === "model_not_tool_capable"
  ) {
    return detail as ModelNotToolCapableDetail;
  }
  return null;
}

/**
 * GET /admin/ai/models?provider=<p>
 * Always returns 200; provider errors surface via warning + empty models list.
 */
export async function getAiModels(provider: string): Promise<ModelsResponse> {
  return apiClient.get<ModelsResponse>(
    `/admin/ai/models?provider=${encodeURIComponent(provider)}`,
  );
}

/** POST /admin/ai/keys/validate reply (EPIC-115). Always HTTP 200 unless 422/429. */
export type ValidateKeyResponse =
  | { provider: string; ok: true; models: AiModelInfo[] }
  | { provider: string; ok: false; models: AiModelInfo[]; message: string };

/**
 * POST /admin/ai/keys/validate (EPIC-115)
 * Tests the typed key without storing it; omit `apiKey` to test the stored
 * key. The key travels in the POST body only, never a query string.
 */
export async function validateAiKey(
  provider: string,
  apiKey?: string,
): Promise<ValidateKeyResponse> {
  const body: { provider: string; api_key?: string } = { provider };
  if (apiKey) body.api_key = apiKey;
  return apiClient.post<ValidateKeyResponse>("/admin/ai/keys/validate", body, {
    timeout: LONG_TIMEOUT_MS,
  });
}

/**
 * POST /admin/ai/models/verify
 * Always returns 200 — success and failure are both carried in the body
 * (`ok: true|false`), never a thrown error, so a stale/retired model can be
 * checked without wiring extra catch handling at every call site.
 * LONG_TIMEOUT_MS: this is a live, minimal forced tool call against the
 * provider (team_plan.md), the same class of upstream round-trip as
 * `generateNarrative`/`postAiQuery` in loggerApi.ts.
 */
export async function verifyModel(
  provider: string,
  model: string,
): Promise<VerifyModelResult> {
  return apiClient.post<VerifyModelResult>(
    "/admin/ai/models/verify",
    { provider, model },
    { timeout: LONG_TIMEOUT_MS },
  );
}

/**
 * POST /admin/ai/playground
 * Returns PlaygroundResponse on success; throws on 502/429/503.
 */
export async function postPlayground(
  payload: PlaygroundPayload,
): Promise<PlaygroundResponse> {
  return apiClient.post<PlaygroundResponse>("/admin/ai/playground", payload);
}
